import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { VacancyDataEditForm } from '../components/vacancies/VacancyDataEditForm';
import { AiAssistantPanel, RequirementsPanel } from '../components/vacancies/ai-assistant';
import { useVacancyStore } from '../store/vacancyStore';
import { useBrandingStore } from '../store/brandingStore';
import { apiService } from '../services/apiService';
import { fetchHHIndustries, fetchHHSpecializations } from '../services/hhDictionariesService';
import { mapApiLevelToUi, mapLevelToMinScore, UI_LEVEL_FALLBACK } from '../utils/vacancies/mapping';
import toast from 'react-hot-toast';
import type { ChatMessage, VacancyFieldStatus, RequirementItem } from '../components/vacancies/ai-assistant';
import { VACANCY_FIELDS } from '../components/vacancies/ai-assistant';

export default function CreateVacancyPage() {
  const navigate = useNavigate();
  const { setSelectedVacancyById, setSelectedVacancy } = useVacancyStore();
  const brandingData = useBrandingStore(state => state.data);
  const [showAiPanel, setShowAiPanel] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [generatedData, setGeneratedData] = useState<any>(null);
  const [resetKey, setResetKey] = useState(0);
  const [hhImportId, setHhImportId] = useState<string | null>(null);
  // Main HH vacancy selected from AI assistant's list (ТОЛЬКО список своих
  // опубликованных вакансий, не URL-импорт). Populated into the form's "Основная
  // вакансия" dropdown so the position gets linked and dialogs/responses flow against it.
  // Separate from hhImportId (which triggers a full form overwrite).
  const [mainHhVacancyId, setMainHhVacancyId] = useState<string | null>(null);
  const industryListRef = useRef<string[]>([]);
  const specializationListRef = useRef<string[]>([]);
  const aboutCompanyRef = useRef<string>('');
  const msgIdRef = useRef(0);

  // Requirements panel state
  const [showRequirementsPanel, setShowRequirementsPanel] = useState(false);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsReviewed, setRequirementsReviewed] = useState(false);
  const [panelRequirements, setPanelRequirements] = useState<{
    required: RequirementItem[];
    optional: RequirementItem[];
  }>({ required: [], optional: [] });

  // Ref to get current form data for manual vacancy analysis
  const formDataRef = useRef<any>(null);

  React.useEffect(() => {
    setSelectedVacancy(null);
    setSelectedVacancyById(null);
    // Pre-load industry & specialization lists for AI prompt
    fetchHHIndustries().then(industries => {
      industryListRef.current = industries.map(i => i.name);
    }).catch(() => {});
    fetchHHSpecializations().then(specs => {
      specializationListRef.current = specs.map(s => s.name);
    }).catch(() => {});
    apiService.getMyAccountSettings().then((settings: any) => {
      if (settings?.aboutCompany) {
        aboutCompanyRef.current = settings.aboutCompany;
      }
    }).catch(() => {});
  }, [setSelectedVacancy, setSelectedVacancyById]);

  const nextMsgId = () => `msg-${++msgIdRef.current}`;

  const addMessage = useCallback((msg: Omit<ChatMessage, 'id'>) => {
    const newMsg = { ...msg, id: nextMsgId() };
    setMessages(prev => [...prev, newMsg]);
    return newMsg.id;
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<ChatMessage>) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  }, []);

  const removeMessage = useCallback((id: string) => {
    setMessages(prev => prev.filter(m => m.id !== id));
  }, []);

  const runAiGeneration = useCallback(async (prompt: string) => {
    setIsGenerating(true);

    addMessage({ type: 'user', text: prompt });

    // Build conversation history for the API (only user and assistant text messages)
    const chatHistory: { role: string; content: string }[] = [];
    // Use current messages state + the new user message
    setMessages(prev => {
      for (const msg of prev) {
        if (msg.type === 'user' && msg.text) {
          chatHistory.push({ role: 'user', content: msg.text });
        } else if (msg.type === 'assistant' && msg.text) {
          chatHistory.push({ role: 'assistant', content: msg.text });
        }
      }
      return prev;
    });
    // Add the current prompt (it was just added to messages but setMessages is async)
    chatHistory.push({ role: 'user', content: prompt });

    // Show thinking indicator
    const thinkingId = addMessage({ type: 'thinking' });

    try {
      // Load aboutCompany from account settings (cached in component state)
      const brandingContext: Record<string, any> = {
        companyName: brandingData?.companyName || undefined,
        benefits: brandingData?.benefits || undefined,
        aboutCompany: aboutCompanyRef.current || undefined,
      };
      if (industryListRef.current.length > 0) {
        brandingContext.industryList = industryListRef.current;
      }
      if (specializationListRef.current.length > 0) {
        brandingContext.specializationList = specializationListRef.current;
      }
      // В качестве currentData шлём актуальное состояние формы (а не старый generatedData) —
      // тогда ручные правки пользователя видны backend'у и защищены от перезаписи в
      // VacancyGenerationService.parseChatResponse. Fallback на generatedData, если форма
      // ещё пустая (первое обращение).
      const currentData = formDataRef.current && Object.keys(formDataRef.current).length > 0
        ? { ...generatedData, ...formDataRef.current }
        : generatedData;
      const result = await apiService.chatGenerateVacancy(chatHistory, currentData, brandingContext);
      const data = result.generatedData || {};
      const aiResponse = result.aiResponse;
      const newFields = result.newFields;

      if (brandingContext.aboutCompany && !data.companyDescription) {
        data.companyDescription = brandingContext.aboutCompany;
      } else if (brandingContext.companyName && !data.companyDescription) {
        data.companyDescription = brandingContext.companyName;
      }

      // Remove thinking indicator
      removeMessage(thinkingId);

      // AI text first
      if (aiResponse) {
        addMessage({ type: 'assistant', text: aiResponse });
      }

      // Show overview if there are any filled fields
      if (data && Object.keys(data).length > 0) {
        const finalFields = VACANCY_FIELDS.map(f => {
          const check = checkFieldFromData(f.key, data);
          return { ...f, status: check.filled ? 'filled' as const : 'no_data' as const, value: check.value, details: check.details };
        });
        addMessage({ type: 'overview', fields: finalFields });

        // Если AI случайно вернул requirements в чат-ответе (не должен после изменения
        // промпта, но на всякий случай) — показываем. Автогенерацию через stateless
        // эндпоинт не делаем: требования формируются ТОЛЬКО по кнопке "Далее: требования".
        const requirements = buildRequirements(data);
        if (requirements.required.length > 0 || requirements.optional.length > 0) {
          addMessage({ type: 'requirements', requirements });
        }
      }

      setGeneratedData(data);

    } catch (error: any) {
      removeMessage(thinkingId);
      const backendMessage = error?.response?.data?.message;
      addMessage({
        type: 'done',
        text: backendMessage || 'Ошибка при генерации данных вакансии. Попробуйте ещё раз.',
      });
      toast.error(backendMessage || 'Ошибка при генерации данных вакансии');
    } finally {
      setIsGenerating(false);
    }
  }, [addMessage, removeMessage, generatedData, brandingData]);

  const handleBack = () => {
    navigate('/recruiter/vacancy');
  };

  const handleVacancyCreated = (vacancyId: number) => {
    setSelectedVacancyById(vacancyId);
    navigate(`/recruiter/vacancy/${vacancyId}`);
  };

  const handleSendChatMessage = (text: string) => {
    runAiGeneration(text);
  };

  const handleReset = () => {
    setMessages([]);
    setGeneratedData(null);
    setIsGenerating(false);
    setHhImportId(null);
    setMainHhVacancyId(null);
    setShowRequirementsPanel(false);
    setPanelRequirements({ required: [], optional: [] });
    setRequirementsReviewed(false);
    setResetKey(k => k + 1);
  };

  const handleCollapse = () => {
    setShowAiPanel(false);
  };

  const handleRequirementsUpdate = (required: RequirementItem[], optional: RequirementItem[]) => {
    // Update in chat messages (for AI panel display)
    setMessages(prev => prev.map(m =>
      m.type === 'requirements' ? { ...m, requirements: { required, optional } } : m
    ));

    // Update panel requirements state
    setPanelRequirements({ required, optional });
  };

  const handleFieldClick = useCallback((fieldKey: string) => {
    const el = document.querySelector(`[data-field-id="${fieldKey}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Highlight pulse effect
    el.classList.add('ai-field-highlight');
    setTimeout(() => el.classList.remove('ai-field-highlight'), 1500);
  }, []);

  // Called when "Создать вакансию" button is clicked - show requirements panel
  const handleShowRequirements = useCallback(async (formData: any) => {
    formDataRef.current = formData;

    // Check if AI was used and requirements are already available
    const requirementsMsg = messages.find(m => m.type === 'requirements');
    if (requirementsMsg?.requirements) {
      // Use AI-generated requirements
      setPanelRequirements({
        required: requirementsMsg.requirements.required,
        optional: requirementsMsg.requirements.optional,
      });
      setShowRequirementsPanel(true);
      setShowAiPanel(false);
      return;
    }

    // Manual vacancy - ask AI to analyze and extract requirements
    setShowRequirementsPanel(true);
    setShowAiPanel(false);
    setRequirementsLoading(true);

    try {
      // Используем тот же stateless эндпоинт, что и "AI" кнопка на странице существующей
      // вакансии — один код, один промпт, один результат. Раньше здесь был вызов
      // chatGenerateVacancy через AI-чат, который часто не возвращал requirements и
      // срезал их до 5. Теперь поведение идентично regeneratePositionAiRequirements.
      const weighted = await apiService.generateAiRequirementsStateless({
        title: formData.title,
        jobDescription: formData.jobDescription,
        level: formData.level,
        skillGroups: formData.skills,
      });

      const parsed = JSON.parse(weighted);
      const genReqs = {
        required: (parsed.required || []).map((r: any, i: number) => ({
          id: `req-${i}`,
          text: r.text,
          weight: r.weight ?? 7,
        })),
        optional: (parsed.optional || []).map((r: any, i: number) => ({
          id: `opt-${i}`,
          text: r.text,
          weight: r.weight ?? 3,
        })),
      };
      setPanelRequirements(genReqs);
    } catch (error) {
      console.error('Error generating requirements:', error);
      toast.error('Не удалось сгенерировать требования. Вы можете добавить их вручную.');
      setPanelRequirements({ required: [], optional: [] });
    } finally {
      setRequirementsLoading(false);
    }
  }, [messages, brandingData]);

  const hasAiSession = messages.length > 0;

  // Determine which right panel to show. Wrapped in AnimatePresence so switching
  // between RequirementsPanel and AiAssistantPanel (and closing either) animates.
  const PANEL_MOTION = {
    initial: { opacity: 0, x: 24 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 24 },
    transition: { duration: 0.22, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
  };
  const rightPanel = (showRequirementsPanel || showAiPanel) ? (
    <AnimatePresence mode="wait" initial={false}>
      {showRequirementsPanel ? (
        <motion.div key="req-panel" className="w-[420px] h-full" {...PANEL_MOTION}>
          <RequirementsPanel
            required={panelRequirements.required}
            optional={panelRequirements.optional}
            onUpdate={handleRequirementsUpdate}
            isLoading={requirementsLoading}
            onCollapse={() => {
              setShowRequirementsPanel(false);
              setShowAiPanel(true);
              // Re-arm the gate so next "Создать" re-opens the panel for another review.
              setRequirementsReviewed(false);
            }}
          />
        </motion.div>
      ) : showAiPanel ? (
        <motion.div key="ai-panel" className="w-[420px] h-full" {...PANEL_MOTION}>
          <AiAssistantPanel
            messages={messages}
            isGenerating={isGenerating}
            onSendMessage={handleSendChatMessage}
            onReset={handleReset}
            onCollapse={handleCollapse}
            onFieldClick={handleFieldClick}
            onImportFromHH={(id) => setHhImportId(id)}
            onSelectMainHhVacancy={(id) => setMainHhVacancyId(id)}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  ) : undefined;

  // Extract current requirements for form (both from panel and AI messages)
  const currentRequirements = panelRequirements.required.length > 0 || panelRequirements.optional.length > 0
    ? {
        required: panelRequirements.required.map(r => r.text).filter(Boolean),
        optional: panelRequirements.optional.map(r => r.text).filter(Boolean),
      }
    : (() => {
        const requirementsMsg = messages.find(m => m.type === 'requirements');
        return requirementsMsg?.requirements ? {
          required: requirementsMsg.requirements.required.map(r => r.text).filter(Boolean),
          optional: requirementsMsg.requirements.optional.map(r => r.text).filter(Boolean),
        } : undefined;
      })();

  // Full requirements with weights — нужны VacancyDataEditForm для сохранения через save.
  const currentRequirementsWithWeights = panelRequirements.required.length > 0 || panelRequirements.optional.length > 0
    ? panelRequirements
    : (() => {
        const requirementsMsg = messages.find(m => m.type === 'requirements');
        return requirementsMsg?.requirements || { required: [], optional: [] };
      })();

  return (
    <VacancyDataEditForm
      key={resetKey}
      onBack={handleBack}
      onSaveSuccess={handleVacancyCreated}
      generatedData={generatedData}
      showCollapsedAiSidebar={!showAiPanel && !showRequirementsPanel}
      onExpandAiPanel={() => { setShowAiPanel(true); setShowRequirementsPanel(false); }}
      rightPanel={rightPanel}
      aiRequirements={currentRequirements}
      aiRequirementsWithWeights={currentRequirementsWithWeights}
      onBeforeCreate={handleShowRequirements}
      requirementsReviewed={requirementsReviewed}
      onRequirementsReviewed={() => setRequirementsReviewed(true)}
      hhImportVacancyId={hhImportId}
      initialHhVacancyLink={mainHhVacancyId}
      onFormDataChange={(data) => { formDataRef.current = data; }}
    />
  );
}

// Helper: check if field has data based on generated response
function checkFieldFromData(fieldKey: string, data: any): { filled: boolean; value: string; details?: { label: string; value: string }[] } {
  if (!data) data = {};

  switch (fieldKey) {
    case 'title': {
      return {
        filled: !!data.title,
        value: data.title || '',
        details: [
          { label: 'Название', value: data.title || '' },
          { label: 'Специализация', value: data.specialization || '' },
        ],
      };
    }
    case 'description': {
      const desc = data.jobDescription || data.suggestedDescription || '';
      const clean = desc.replace(/<[^>]*>/g, '').substring(0, 100);
      return {
        filled: !!desc,
        value: clean,
        details: [
          { label: 'Описание', value: clean ? clean + '...' : '' },
        ],
      };
    }
    case 'skills': {
      const skillNames = data.skills?.flatMap((g: any) => g.skills || []) || data.topics || data.tags || [];
      return {
        filled: skillNames.length > 0,
        value: skillNames.join(', '),
        details: [
          { label: 'Навыки', value: skillNames.join(', ') || '' },
        ],
      };
    }
    case 'companyDescription': {
      const desc = data.companyDescription || '';
      const clean = desc.replace(/<[^>]*>/g, '').substring(0, 100);
      return {
        filled: !!desc,
        value: clean,
        details: [
          { label: 'О компании', value: clean ? clean + '...' : '' },
        ],
      };
    }
    case 'industry': {
      return {
        filled: !!(data.industry || data.specialization),
        value: data.industry || '',
        details: [
          { label: 'Отрасль', value: data.industry || '' },
          { label: 'Специализация', value: data.specialization || '' },
        ],
      };
    }
    case 'experience': {
      const expLabels: Record<string, string> = {
        noExperience: 'Без опыта',
        between1And3: 'От 1 до 3 лет',
        between3And6: 'От 3 до 6 лет',
        moreThan6: 'Более 6 лет',
      };
      const levelLabels: Record<string, string> = {
        JUNIOR: 'Junior',
        MIDDLE: 'Middle',
        SENIOR: 'Senior',
        LEAD: 'Lead',
      };
      const hasLevel = !!data.level;
      const hasExpInPos = data.experienceInPosition?.length > 0;
      const filled = hasLevel && hasExpInPos;
      const expDisplay = (data.experienceInPosition || []).map((e: string) => expLabels[e] || e).join(', ');
      return {
        filled,
        value: '',
        details: [
          { label: 'Уровень', value: levelLabels[data.level] || data.level || '' },
          { label: 'Опыт в должности', value: expDisplay },
        ],
      };
    }
    case 'salary': {
      const hasSalary = !!(data.salaryFrom || data.salaryTo);
      const from = data.salaryFrom ? Number(data.salaryFrom).toLocaleString('ru-RU') : '';
      const to = data.salaryTo ? Number(data.salaryTo).toLocaleString('ru-RU') : '';
      const salaryStr = from && to ? `${from} — ${to} ₽` : from ? `от ${from} ₽` : to ? `до ${to} ₽` : '';
      const contractLabels: Record<string, string> = {
        tk_rf: 'По ТК', ip: 'ИП', self_employed: 'Самозанятый',
        individual: 'Физлицо (ГПХ)', accredited_it: 'Аккредитованная IT',
      };
      const contractDisplay = (data.employmentContractTypes || [])
        .map((t: string) => contractLabels[t] || t).join(', ');
      return {
        filled: hasSalary,
        value: '',
        details: [
          { label: 'Зарплата', value: salaryStr },
          { label: 'Тип занятости', value: contractDisplay },
        ],
      };
    }
    case 'region': {
      const cities = data.cities || [];
      return {
        filled: cities.length > 0,
        value: cities.join(', '),
        details: [
          { label: 'Регионы', value: cities.join(', ') || '' },
        ],
      };
    }
    case 'workFormat': {
      return {
        filled: !!(data.workFormat || data.workSchedule),
        value: '',
        details: [
          { label: 'Формат', value: data.workFormat || '' },
          { label: 'График', value: data.workSchedule || '' },
        ],
      };
    }
    case 'socialPackage': {
      return {
        filled: !!data.socialPackageText,
        value: data.socialPackageText || '',
        details: [
          { label: '', value: data.socialPackageText || 'Льготы и бенефиты' },
        ],
      };
    }
    default:
      return { filled: false, value: '' };
  }
}

// Helper: build requirements from generated data
function buildRequirements(data: any): { required: RequirementItem[]; optional: RequirementItem[] } {
  const required: RequirementItem[] = [];
  const optional: RequirementItem[] = [];

  if (!data) return { required, optional };

  // Use requirements from new endpoint if available
  if (data.requirements) {
    (data.requirements.required || []).forEach((text: string, i: number) => {
      required.push({ id: `req-${i}`, text, weight: 7 });
    });
    (data.requirements.optional || []).forEach((text: string, i: number) => {
      optional.push({ id: `opt-${i}`, text, weight: 3 });
    });
    return { required, optional };
  }

  // Fallback: use topics/tags from old endpoint
  const topics = data.topics || [];
  topics.slice(0, 4).forEach((topic: string, i: number) => {
    required.push({ id: `req-${i}`, text: topic, weight: 7 });
  });

  const tags = data.tags || [];
  tags.slice(0, 2).forEach((tag: string, i: number) => {
    optional.push({ id: `opt-${i}`, text: tag, weight: 3 });
  });

  return { required, optional };
}
